/**
 * Contrato tipado de progresso operacional do stream da Snap (épico #21124).
 *
 * O MESMO contrato vale no backend (`domains/reports/services/snap_progress.py`),
 * no processor, no frame WebSocket `report_partial` e aqui. Progresso é metadado
 * **operacional em texto claro** — contadores de passo, sem conteúdo de
 * relatório e sem PII. Por isso:
 *
 * - viaja e é persistido sob `report_status.snap_progress`, nunca dentro do
 *   payload cifrado;
 * - **nunca** entra em `report_search_args` (que alimenta exibição, HMAC/n-grams,
 *   nome do relatório, identidade de merge e redispatch);
 * - não é conteúdo, identidade nem argumento de busca do relatório.
 */

export const SNAP_PROGRESS_VERSION = 1 as const;

/** Chave sob a qual o progresso durável mora dentro de `report_status`. */
export const SNAP_PROGRESS_KEY = "snap_progress" as const;

export interface SnapProgressMetadata {
  version: 1;
  sequence: number;
  completed_steps: number;
  total_steps?: number;
  remaining_steps?: number;
  /** Fração na escala documentada 0..1. */
  progress?: number;
}

/** Teto defensivo: contador acima disso é payload corrompido, não progresso. */
const MAX_STEPS = 1_000_000;

function asNonNegativeInt(value: unknown): number | undefined {
  if (typeof value === "boolean" || value === null || value === undefined) return undefined;
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  if (!Number.isFinite(numeric) || !Number.isInteger(numeric)) return undefined;
  if (numeric < 0 || numeric > MAX_STEPS) return undefined;
  return numeric;
}

/**
 * Normaliza a fração para 0..1, ou devolve `undefined`.
 *
 * A Snap já entregou esse campo como `"N/M"`, como número 0..1 e como 0..100.
 * Qualquer coisa fora disso é omitida em vez de exibida errado.
 */
export function normalizeProgressFraction(
  raw: unknown,
  completedSteps?: number,
  totalSteps?: number,
): number | undefined {
  let fraction: number | undefined;

  if (typeof raw === "string") {
    const text = raw.trim();
    if (text.includes("/")) {
      const [rawNum, rawDen] = text.split("/");
      const num = Number(rawNum);
      const den = Number(rawDen);
      if (Number.isFinite(num) && Number.isFinite(den) && den > 0) fraction = num / den;
    } else if (text !== "") {
      const parsed = Number(text.replace("%", "").trim());
      if (Number.isFinite(parsed)) fraction = text.endsWith("%") ? parsed / 100 : parsed;
    }
  } else if (typeof raw === "number" && Number.isFinite(raw)) {
    fraction = raw;
  }

  if (fraction !== undefined && fraction > 1) {
    fraction = fraction <= 100 ? fraction / 100 : undefined;
  }

  if (fraction === undefined && completedSteps !== undefined && totalSteps) {
    fraction = completedSteps / totalSteps;
  }

  if (fraction === undefined || !Number.isFinite(fraction)) return undefined;
  return Math.min(Math.max(fraction, 0), 1);
}

/**
 * Valida e canoniza um metadado de progresso vindo do WebSocket ou do banco.
 *
 * Devolve `null` quando não dá para confiar no conjunto. Campos opcionais
 * malformados são descartados individualmente — `remaining_steps` é sempre
 * DERIVADO (`max(total - completed, 0)`), nunca aceito como veio.
 */
export function parseSnapProgress(value: unknown): SnapProgressMetadata | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (raw.version !== SNAP_PROGRESS_VERSION) return null;

  const sequence = asNonNegativeInt(raw.sequence);
  const completed = asNonNegativeInt(raw.completed_steps);
  if (sequence === undefined || completed === undefined) return null;

  let total = asNonNegativeInt(raw.total_steps);
  // Total incoerente com o avanço já observado: preserva o avanço (informação
  // dura) e descarta o total, em vez de recusar o progresso inteiro.
  if (total !== undefined && completed > total) total = undefined;

  const parsed: SnapProgressMetadata = {
    version: SNAP_PROGRESS_VERSION,
    sequence,
    completed_steps: completed,
  };
  if (total !== undefined) {
    parsed.total_steps = total;
    parsed.remaining_steps = Math.max(total - completed, 0);
  }
  const fraction = normalizeProgressFraction(raw.progress, completed, total);
  if (fraction !== undefined) parsed.progress = fraction;
  return parsed;
}

/** True quando `incoming` deve substituir `stored` (monotônico por `sequence`). */
export function isNewerProgress(
  stored: SnapProgressMetadata | null | undefined,
  incoming: SnapProgressMetadata | null | undefined,
): boolean {
  if (!incoming) return false;
  if (!stored) return true;
  return incoming.sequence > stored.sequence;
}

/** Lê `report_status.snap_progress` de um relatório, já validado. */
export function readPersistedProgress(reportStatus: unknown): SnapProgressMetadata | null {
  if (!reportStatus || typeof reportStatus !== "object") return null;
  return parseSnapProgress((reportStatus as Record<string, unknown>)[SNAP_PROGRESS_KEY]);
}
